import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { NewsCard, NewsCardSkeleton } from './NewsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

/**
 * Paginated grid of published articles for a set of /public/articles filters
 * (category, district, division, region). Used by the category and Rajya/Sahar pages.
 */
export function ArticleFeed({ params, emptyText }) {
  const { isHindi } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // Filters can change while a request is in flight (e.g. tapping through mandals);
  // only the newest request may update the list
  const requestId = useRef(0);
  const paramsKey = JSON.stringify(params);

  const fetchPage = async (skip) => {
    const id = ++requestId.current;
    skip === 0 ? setLoading(true) : setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/public/articles`, {
        params: { ...params, limit: PAGE_SIZE, skip },
      });
      if (id !== requestId.current) return;
      const page = res.data.data || res.data;
      setArticles((prev) => (skip === 0 ? page : [...prev, ...page]));
      setHasMore(page.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    setArticles([]);
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  if (loading) {
    return (
      <div className="rule-grid cols-3">
        {[...Array(6)].map((_, i) => <NewsCardSkeleton key={i} />)}
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="text-center py-16">
        <p className={`text-[var(--ink-muted)] text-lg font-body ${isHindi ? 'font-hindi' : ''}`}>{emptyText}</p>
      </div>
    );
  }

  return (
    <>
      <div className="rule-grid cols-3">
        {articles.map((article) => (
          <NewsCard key={article.article_id} article={article} />
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8 sm:mt-10">
          <button
            onClick={() => fetchPage(articles.length)}
            disabled={loadingMore}
            className={`btn-outline w-full sm:w-auto ${isHindi ? 'font-hindi' : ''}`}
          >
            {loadingMore
              ? (isHindi ? 'लोड हो रहा है...' : 'Loading...')
              : (isHindi ? 'और समाचार देखें' : 'Load More')}
          </button>
        </div>
      )}
    </>
  );
}
